import { HttpContext, httpResource } from '@angular/common/http';
import { Service, computed } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { ResponseApi } from '../../../core/dtos/response-api';
import { HANDLE_HTTP_ERROR_LOCALLY } from '../../../core/http/http-context-tokens';
import { slugDeServico } from '../../../shared/utils/slug.util';
import { CategoriaServicosCor } from '../models/categoria-servicos.model';
import { TodosServicosCategoria, TodosServicosItem } from '../models/todos-servicos.model';
import { corDaCategoria } from './identidade-categoria.util';

const RECURSO = `${environment.apiUrl}/sai/cartaservico/publico/servico`;

/** A foto do card quando o serviço não tem imagem, ou quando ela não carrega. */
export const IMAGEM_PADRAO_MAIS_PROCURADOS = 'assets/images/servicos-mais-procurados.png';

/** Quantos cards a seção da homepage mostra. */
const QUANTIDADE = 4;

/** O erro fica local: sem ranking, a seção completa com o catálogo, sem modal. */
const ERRO_LOCAL = () => new HttpContext().set(HANDLE_HTTP_ERROR_LOCALLY, true);

/** Um item de `GET publico/servico/mais-procurados`: só o código e a posição. */
export interface ServicoMaisProcuradoResponse {
  codServicoSrv: number;
  numPosicao: number;
}

/** O card da seção "Serviços mais procurados". */
export interface ServicoMaisProcurado {
  /** O código do serviço em texto. */
  id: string;
  titulo: string;
  descricao: string;
  /** A página do serviço: `/{slug-do-nome}`. */
  link: string;
  /** A imagem pública do serviço, ou a foto padrão. */
  imagem: string;
  /** A cor da primeira categoria do serviço, na paleta de `data-categoria-servicos-cor`. */
  cor: CategoriaServicosCor;
  inicial: string;
}

/**
 * O ranking da seção "Serviços mais procurados" da homepage: os códigos dos serviços mais
 * abertos no portal nos últimos 30 dias, em ordem.
 *
 * A API devolve só códigos — nome, resumo, link, cor e imagem vêm do catálogo público, que
 * `montarMaisProcurados` cruza. O portal não envia o órgão: o back-end o resolve pelo host.
 */
@Service()
export class ServicosMaisProcuradosService {
  private readonly rankingApi = httpResource<ResponseApi<ServicoMaisProcuradoResponse[]>>(() => ({
    url: `${RECURSO}/mais-procurados`,
    context: ERRO_LOCAL(),
  }));

  readonly carregando = computed(() => this.rankingApi.isLoading());

  /**
   * Os códigos em ordem de posição. Em falha, lista vazia: a seção trata como "sem ranking" e
   * completa com o catálogo.
   *
   * O `error()` é consultado antes do `value()`, que lança em estado de erro.
   */
  readonly codigos = computed<readonly number[]>(() => {
    if (this.rankingApi.error() !== undefined) return [];

    return [...(this.rankingApi.value()?.data ?? [])]
      .sort((a, b) => a.numPosicao - b.numPosicao)
      .map((item) => item.codServicoSrv);
  });
}

/**
 * Os cards da seção: primeiro os serviços do ranking que o catálogo tem, na ordem do ranking;
 * o que faltar para `quantidade` vem do catálogo, na ordem editorial (categorias pela ordem do
 * adm e, dentro de cada uma, a ordem em que o catálogo traz os serviços).
 *
 * Código do ranking fora do catálogo — o serviço sem categoria publicável, por exemplo — é
 * descartado sem erro, e o lugar dele fica para o próximo. Um serviço em duas categorias
 * aparece uma vez, com a cor da primeira.
 */
export function montarMaisProcurados(
  codigos: readonly number[],
  categorias: readonly TodosServicosCategoria[],
  quantidade = QUANTIDADE,
): ServicoMaisProcurado[] {
  const porServico = new Map<string, { item: TodosServicosItem; cor: string | undefined }>();

  for (const categoria of categorias) {
    for (const item of categoria.servicos) {
      if (!porServico.has(item.id)) porServico.set(item.id, { item, cor: categoria.cor });
    }
  }

  const escolhidos: string[] = [];

  for (const id of [...codigos.map(String), ...porServico.keys()]) {
    if (escolhidos.length >= quantidade) break;
    if (porServico.has(id) && !escolhidos.includes(id)) escolhidos.push(id);
  }

  return escolhidos.map((id) => {
    const { item, cor } = porServico.get(id)!;

    return {
      id,
      titulo: item.titulo,
      descricao: item.descricao,
      link: item.link ?? `/${slugDeServico(item.titulo)}`,
      imagem: item.possuiImagem ? `${RECURSO}/${id}/imagem` : IMAGEM_PADRAO_MAIS_PROCURADOS,
      cor: corDaCategoria(cor),
      inicial: item.titulo.trim().charAt(0).toUpperCase(),
    };
  });
}
